import React from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { TTranslateFn } from '@kurtosys/types/common/TTranslateFn.js';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as moment from 'moment';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import Actions from '../Actions/Actions.js';
import CalendarInput from '../CalendarInput/CalendarInput.js';
import { ICalendarInputProps } from '../CalendarInput/models/index.js';
import Dropdown from '../Dropdown/Dropdown.js';
import { IDropdownItem } from '../Dropdown/models/IDropdownItem.js';
import { IDropdownProps } from '../Dropdown/models/index.js';
import Input from '../Input/Input.js';
import { INumberPickerProps } from '../NumberPicker/models/index.js';
import NumberPicker from '../NumberPicker/NumberPicker.js';
import { ITranslationContextValue } from '../Translate/models/index.js';
import { TranslationContext } from '../Translate/Translate.js';

import {
	IDateRangeInputConfiguration,
	IDateRangeInputLabels,
	IDateRangeInputProps,
	IDateRangeInputState,
} from './models/index.js';
import CalendarWrapper from './styledComponents/CalendarWrapper.js';
import ClearFieldIcon from './styledComponents/ClearFieldIcon.js';
import Field from './styledComponents/Field.js';
import FieldLabel from './styledComponents/FieldLabel.js';
import FieldsWrapper from './styledComponents/FieldsWrapper.js';
import SelectedDate from './styledComponents/SelectedDate.js';
import SelectedDateWrapper from './styledComponents/SelectedDateWrapper.js';
import SelectedYear from './styledComponents/SelectedYear.js';
import Wrapper from './styledComponents/Wrapper.js';
import { DATE_RANGE_INPUT_CATEGORY_KEY, DATE_RANGE_INPUT_COMPONENT_KEY } from './DateRangeInput.meta.js';

export type TActiveDateField = 'start' | 'end' | undefined;
export type TCalendarView = 'calendar' | 'year';

export class DateRangeInput extends ComponentBase<
	IDateRangeInputProps,
	IDateRangeInputState,
	IDateRangeInputConfiguration
> {
	categoryKey = DATE_RANGE_INPUT_CATEGORY_KEY;
	componentKey = DATE_RANGE_INPUT_COMPONENT_KEY;

	dateField: IDropdownItem | undefined;
	selectedDate: string | undefined;
	startDate = '';
	endDate = '';
	activeField: TActiveDateField = 'start';
	calendarView: TCalendarView = 'calendar';

	constructor(props: IDateRangeInputProps) {
		super(props);

		this.startDate = props.startDate || '';
		this.endDate = props.endDate || '';
		this.dateField = props.selectedDateField;
		this.selectedDate = this.startDate;

		makeObservable(this, {
			dateField: observable,
			selectedDate: observable,
			startDate: observable,
			endDate: observable,
			activeField: observable,
			calendarView: observable,
			setActiveField: action,
			setCalendarView: action,
			handleChange: action,
			setStartDate: action,
			setEndDate: action,
			labels: computed,
			minimumDate: computed,
			selectedYear: computed,
			hideInputFieldSection: computed,
			calendarInputProps: computed,
			dateFieldProps: computed,
			handleDateFieldChange: action,
			yearInputProps: computed,
			handleYearInputChange: action,
		});
	}

	setActiveField(activeField?: TActiveDateField) {
		this.activeField = activeField;
		if (this.activeField === 'start') {
			this.selectedDate = this.startDate;
		}
		if (this.activeField === 'end') {
			this.selectedDate = this.endDate;
		}
	}

	setCalendarView(view: TCalendarView) {
		this.calendarView = view;
	}

	handleChange() {
		const { onChange } = this.props;
		if (onChange) {
			onChange(this.startDate, this.endDate, this.dateField);
		}
	}

	setStartDate(value = '') {
		this.startDate = value;
		this.selectedDate = this.startDate;
		this.handleChange();
	}

	setEndDate(value = '') {
		this.endDate = value;
		this.selectedDate = this.endDate;
		this.handleChange();
	}

	get labels(): Required<IDateRangeInputLabels> {
		const defaultLabels: Required<IDateRangeInputLabels> = {
			dateField: 'Date Field',
			startDate: 'Start Date',
			endDate: 'End Date',
		};
		return { ...defaultLabels, ...(this.props.labels || {}) };
	}

	get minimumDate(): string {
		if (this.activeField === 'end' && this.startDate) {
			return this.startDate;
		}
		return (this.props && this.props.minumumDate) || '1970-01-01';
	}

	get maximumDate(): string {
		if (this.activeField === 'start' && this.endDate) {
			return this.endDate;
		}
		return (this.props && this.props.maximumDate) || '2099-01-01';
	}

	get selectedYear() {
		const calendarValue = moment.utc(this.selectedDate || Date.now());
		return parseInt(calendarValue.format('YYYY'), 10);
	}

	get hideInputFieldSection(): boolean {
		return (this.configuration && this.props.hideInputFieldSection) || false;
	}

	get calendarInputProps(): ICalendarInputProps {
		const { suppressAnalytics = true } = this.props;
		return {
			isFlat: true,
			suppressAnalytics: suppressAnalytics,
			value: this.selectedDate,
			minimumDate: this.minimumDate,
			dayNames: this.props && this.props.dayNames,
			monthNames: this.props && this.props.monthNames,
			// 86400000 represents 1 day in milliseconds
			maximumDate:
				this.maximumDate === '2099-01-01'
					? '2099-01-01'
					: moment.utc(Date.parse(this.maximumDate) + 86400000).format('YYYY-MM-DD'),
			onChange: (value: string) => {
				if (this.activeField === 'start') {
					this.setStartDate(value);
				} else if (this.activeField === 'end') {
					this.setEndDate(value);
				}
				this.handleChange();
			},
		};
	}

	get dateFieldProps(): IDropdownProps {
		const { dateFieldItems = [] } = this.props;
		return {
			selectedItem: this.dateField,
			items: dateFieldItems,
			placeholder: this.labels.dateField,
			onChange: this.handleDateFieldChange,
		};
	}

	handleDateFieldChange = (value: IDropdownItem | IDropdownItem[]) => {
		if (Array.isArray(value)) {
			this.dateField = value.length > 0 ? value[0] : undefined;
		} else {
			this.dateField = value;
		}
		this.handleChange();
	};

	get yearInputProps(): INumberPickerProps {
		return {
			value: this.selectedYear,
			startValue: parseInt(moment.utc(this.minimumDate).format('YYYY'), 10),
			endValue: parseInt(moment.utc(this.maximumDate).format('YYYY'), 10),
			onChange: this.handleYearInputChange,
		};
	}

	handleYearInputChange = (value?: number) => {
		if (this.selectedDate && value) {
			const newDate = new Date(this.selectedDate).setFullYear(value);
			this.selectedDate = moment.utc(newDate).format('YYYY-MM-DD');
		} else if (value) {
			const newDate = new Date().setFullYear(value);
			this.selectedDate = moment.utc(newDate).format('YYYY-MM-DD');
		}
		this.calendarView = 'calendar';
	};

	formatDateInput = (date: string, locale: string, translate: TTranslateFn | undefined) => {
		const { formatting } = this.props;
		const formatOptions = formatting && formatting.dateInputFormat;
		if (formatOptions) {
			return common.commonUtils.formatDateTime(locale, formatOptions, date, translate);
		}
		return date;
	};

	formatSelectedDate = (date: string, locale: string, translate: TTranslateFn | undefined) => {
		const { formatting } = this.props;
		const formatOptions = formatting && formatting.selectedDateFormat;
		if (formatOptions) {
			return common.commonUtils.formatDateTime(locale, formatOptions, date, translate);
		}
		return date;
	};

	translateValue(translationContext: ITranslationContextValue, value?: string) {
		return value ? translationContext.translate(value) : value;
	}

	render() {
		const { className, dateFieldItems, actionsProps } = this.props;
		const setYearView = () => this.setCalendarView('year');
		const setCalendarView = () => this.setCalendarView('calendar');

		const { selectedDate, startDate, endDate, activeField, calendarView } = this;

		return (
			<Wrapper className={className} key={`${activeField}-${startDate}-${endDate}`}>
				<TranslationContext.Consumer>
					{(translationContext: ITranslationContextValue) => {
						const { culture, translate } = translationContext;

						return (
							<React.Fragment>
								{!this.hideInputFieldSection && (
									<FieldsWrapper>
										{!common.commonUtils.isNullOrEmpty(dateFieldItems) && (
											<Field>
												<FieldLabel value={this.labels.dateField} />
												<Dropdown {...this.dateFieldProps} />
											</Field>
										)}
										<Field>
											<FieldLabel value={this.labels.startDate} />
											<Input
												className={activeField === 'start' ? 'active-field' : ''}
												readOnly={true}
												placeholder={this.labels.startDate}
												value={this.formatDateInput(startDate, culture, translate)}
												onClick={() => this.setActiveField('start')}
											/>
											{startDate && (
												<ClearFieldIcon
													asset={'base.dateRangeInput.clearField'}
													onClick={() => this.setStartDate()}
												/>
											)}
										</Field>
										<Field>
											<FieldLabel value={this.labels.endDate} />
											<Input
												className={activeField === 'end' ? 'active-field' : ''}
												readOnly={true}
												placeholder={this.labels.endDate}
												value={this.formatDateInput(endDate, culture, translate)}
												onClick={() => this.setActiveField('end')}
											/>
											{endDate && (
												<ClearFieldIcon
													asset={'base.dateRangeInput.clearField'}
													onClick={() => this.setEndDate()}
												/>
											)}
										</Field>
										{actionsProps && <Actions {...actionsProps} />}
									</FieldsWrapper>
								)}
								<SelectedDateWrapper>
									<SelectedYear onClick={setYearView}>{this.selectedYear}</SelectedYear>
									<SelectedDate onClick={setCalendarView}>
										{this.formatSelectedDate(selectedDate || '', culture, translate)}
									</SelectedDate>
								</SelectedDateWrapper>
								<CalendarWrapper>
									{calendarView === 'calendar' && <CalendarInput {...this.calendarInputProps} />}
									{calendarView === 'year' && <NumberPicker {...this.yearInputProps} />}
								</CalendarWrapper>
							</React.Fragment>
						);
					}}
				</TranslationContext.Consumer>
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(
	observer(DateRangeInput),
	DATE_RANGE_INPUT_CATEGORY_KEY,
	DATE_RANGE_INPUT_COMPONENT_KEY,
);
